<template>
    <div class="mtrend">
        <div class="mtrend__head">
            <h6 class="mb-0 mr-3">월별 추이</h6>
            <small class="text-muted">매출 · 비용 · 순수익</small>
            <div class="flex-grow-1"></div>
            <div class="mtrend__legend">
                <span><i class="dot" style="background:#2b6cb0"></i>순매출</span>
                <span><i class="dot" style="background:#c0392b"></i>총비용</span>
                <span><i class="dot" style="background:#1e874b"></i>영업이익</span>
            </div>
        </div>

        <div class="mtrend__body">
            <div v-if="!points.length" class="text-center text-muted py-5">
                표시할 데이터가 없습니다.
            </div>
            <template v-else>
                <div class="mtrend__chart">
                    <svg ref="svg" :viewBox="'0 0 ' + W + ' ' + H" preserveAspectRatio="none" class="mtrend__svg">
                        <!-- 가로 눈금 -->
                        <line
                            v-for="(g, i) in gridLines"
                            :key="'g' + i"
                            :x1="PAD_L" :x2="W - PAD_R" :y1="g" :y2="g"
                            stroke="#eef1f4" stroke-width="1"
                        />
                        <!-- 0선 (영업이익이 마이너스일 때 보이게) -->
                        <line
                            :x1="PAD_L" :x2="W - PAD_R" :y1="yOf(0)" :y2="yOf(0)"
                            stroke="#cfd8e0" stroke-width="1" stroke-dasharray="3 3"
                        />

                        <path :d="linePath('netSales')" fill="none" stroke="#2b6cb0" stroke-width="2.5" />
                        <path :d="linePath('totalCost')" fill="none" stroke="#c0392b" stroke-width="2" />
                        <path :d="linePath('opProfit')" fill="none" stroke="#1e874b" stroke-width="2.5" />

                        <!-- 마우스 올린 달 강조 (클릭을 가로채지 않도록 pointer-events 해제) -->
                        <rect
                            v-if="hover !== null"
                            :x="xOf(hover) - slotW / 2" :y="0" :width="slotW" :height="H"
                            fill="#2b6cb0" fill-opacity="0.06"
                            style="pointer-events: none;"
                        />

                        <!-- 점 + 영업이익 값 (마우스를 올리지 않아도 보이게) -->
                        <g v-for="(p, i) in points" :key="'p' + i" style="pointer-events: none;">
                            <circle :cx="xOf(i)" :cy="yOf(p.netSales)" r="3.5" fill="#2b6cb0" />
                            <circle :cx="xOf(i)" :cy="yOf(p.totalCost)" r="3" fill="#c0392b" />
                            <circle :cx="xOf(i)" :cy="yOf(p.opProfit)" r="3.5" fill="#1e874b" />
                            <!-- viewBox가 가로로 늘어나므로 글자는 별도 좌표계에 그린다 -->
                            <g :transform="'translate(' + xOf(i) + ',' + (yOf(p.opProfit) + 16) + ') scale(' + textScaleX + ',1)'">
                                <text
                                    x="0" y="0"
                                    text-anchor="middle"
                                    font-size="11"
                                    font-weight="700"
                                    paint-order="stroke"
                                    stroke="#fff"
                                    stroke-width="3"
                                    stroke-linejoin="round"
                                    :fill="p.opProfit < 0 ? '#c0392b' : '#1e874b'"
                                >{{ short(p.opProfit) }}</text>
                            </g>
                        </g>

                        <!-- 클릭·호버 감지 (가장 위에 한 겹으로 덮는다) -->
                        <rect
                            :x="0" :y="0" :width="W" :height="H"
                            fill="transparent"
                            style="cursor: pointer;"
                            @mousemove="onMove"
                            @mouseleave="hover = null"
                            @click="onClick"
                        />
                    </svg>

                    <div class="mtrend__axis">
                        <span v-for="(p, i) in points" :key="'x' + i" :style="{ left: pctOf(i) + '%' }">
                            <b>{{ monthLabel(p.month) }}</b>
                            <em>{{ short(p.netSales) }}</em>
                        </span>
                    </div>
                </div>

                <!-- 마우스 올린 달의 수치 -->
                <div class="mtrend__detail" :class="{ 'is-empty': hover === null }">
                    <template v-if="hover !== null">
                        <strong class="mr-3">{{ monthLabel(points[hover].month) }}</strong>
                        <span>순매출 <b>{{ fmt(points[hover].netSales) }}</b></span>
                        <span>총비용 <b class="text-danger">{{ fmt(points[hover].totalCost) }}</b></span>
                        <span>영업이익
                            <b :class="points[hover].opProfit >= 0 ? 'text-success' : 'text-danger'">
                                {{ fmt(points[hover].opProfit) }}
                            </b>
                        </span>
                        <span>영업이익률
                            <b>{{ points[hover].netSales ? (points[hover].opProfit / points[hover].netSales * 100).toFixed(1) : '0.0' }}%</b>
                        </span>
                        <span class="ml-auto text-primary">클릭하면 이 달 내역으로 이동 →</span>
                    </template>
                    <span v-else class="text-muted">
                        <i class="fas fa-hand-pointer mr-1"></i>
                        그래프의 달을 클릭하면 그 달의 일별 · 상품별 내역으로 이동합니다.
                    </span>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
/**
 * 월별 매출·비용·순수익 추이
 *
 * 요약장부 피벗이 '계정별로 얼마'를 보여준다면 이건 '달마다 어느 쪽으로 가고 있나'를 본다.
 * 총비용 = 순매출 − 영업이익 (판매비용·매입원가·부가세·고정비를 다 합친 것)
 */
export default {
    name: 'MonthlyTrendChart',
    props: {
        // 서버 월별 집계 [{ month, netSales, sellingCosts, cogs, vat, contribution, grossProfit }]
        monthlySummary: { type: Array, default: () => [] },
        fixedCosts: { type: Array, default: () => [] }
    },
    data() {
        return {
            hover: null,
            svgWidth: 0,      // 실제 렌더 폭 (글자 가로 왜곡 보정용)
            W: 1000,
            H: 240,
            PAD_L: 10,
            PAD_R: 10,
            PAD_T: 16,
            PAD_B: 16
        };
    },
    computed: {
        points() {
            return this.monthlySummary.map(m => {
                const fixed = this.fixedCostOf(m.month);
                const netSales = this.num(m.netSales);
                // 영업이익 = 총이익(GP) − 고정비 + 고정비 부가세 환급분
                // 기타비용(상품 직접비)은 서버 총이익에 이미 반영돼 있다. 여기서 또 빼면 이중 차감
                const opProfit = this.num(m.grossProfit) - fixed.amount + fixed.refund;
                return {
                    month: m.month,
                    netSales,
                    opProfit,
                    totalCost: netSales - opProfit
                };
            });
        },
        maxValue() {
            let max = 0;
            this.points.forEach(p => {
                max = Math.max(max, p.netSales, p.totalCost, p.opProfit);
            });
            return max || 1;
        },
        minValue() {
            let min = 0;
            this.points.forEach(p => {
                min = Math.min(min, p.opProfit, p.totalCost, p.netSales);
            });
            return min;
        },
        gridLines() {
            const out = [];
            for (let i = 0; i <= 4; i++) {
                out.push(this.PAD_T + (this.H - this.PAD_T - this.PAD_B) * (i / 4));
            }
            return out;
        },
        slotW() {
            const n = this.points.length;
            return n > 1 ? (this.W - this.PAD_L - this.PAD_R) / (n - 1) : this.W;
        },
        // preserveAspectRatio="none" 이라 가로로 늘어난다. 글자는 그만큼 되돌려야 안 뭉개진다
        textScaleX() {
            return this.svgWidth > 0 ? this.W / this.svgWidth : 1;
        }
    },
    mounted() {
        this.measure();
        window.addEventListener('resize', this.measure);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.measure);
    },
    methods: {
        num(v) {
            const n = Number(v);
            return isNaN(n) ? 0 : n;
        },
        fmt(v) {
            return Math.round(this.num(v)).toLocaleString() + '원';
        },
        measure() {
            this.$nextTick(() => {
                const el = this.$refs.svg;
                if (el) this.svgWidth = el.getBoundingClientRect().width || 0;
            });
        },
        monthLabel(m) {
            return parseInt(String(m).slice(5, 7), 10) + '월';
        },
        // 그래프 위에 겹쳐 쓰는 값이라 짧게 (1,234만 / 123만 / 12.3만)
        short(v) {
            const n = this.num(v);
            const abs = Math.abs(n);
            const sign = n < 0 ? '-' : '';
            if (abs >= 100000000) return sign + (abs / 100000000).toFixed(1).replace(/\.0$/, '') + '억';
            if (abs >= 10000) return sign + Math.round(abs / 10000).toLocaleString() + '만';
            if (abs === 0) return '0';
            return sign + Math.round(abs).toLocaleString();
        },
        fixedCostOf(month) {
            let amount = 0;
            let refund = 0;
            const doc = (this.fixedCosts || []).find(f => f.month === month);
            if (doc) {
                (doc.items || []).forEach(it => {
                    const v = this.num(it.amount);
                    amount += v;
                    if (it.tax === 'vat') refund += Math.round(v / 11);
                });
            }
            return { amount, refund };
        },
        xOf(i) {
            const n = this.points.length;
            if (n <= 1) return this.W / 2;
            return this.PAD_L + (this.W - this.PAD_L - this.PAD_R) * (i / (n - 1));
        },
        pctOf(i) {
            const n = this.points.length;
            if (n <= 1) return 50;
            return (i / (n - 1)) * 100;
        },
        yOf(v) {
            const span = this.maxValue - this.minValue || 1;
            const inner = this.H - this.PAD_T - this.PAD_B;
            return this.PAD_T + inner * (1 - (v - this.minValue) / span);
        },
        // SVG가 viewBox로 늘어나 있으므로 화면 좌표를 비율로 환산해 가장 가까운 달을 찾는다
        indexAt(evt) {
            const n = this.points.length;
            if (!n) return null;
            const box = evt.currentTarget.getBoundingClientRect();
            if (!box.width) return null;
            const ratio = (evt.clientX - box.left) / box.width;
            // 좌우 여백(PAD)을 감안해 데이터 구간으로 환산
            const inner = (ratio * this.W - this.PAD_L) / (this.W - this.PAD_L - this.PAD_R);
            const idx = Math.round(inner * (n - 1));
            return Math.max(0, Math.min(n - 1, idx));
        },
        onMove(evt) {
            this.hover = this.indexAt(evt);
        },
        onClick(evt) {
            const idx = this.indexAt(evt);
            if (idx === null) return;
            this.$emit('month-click', this.points[idx].month);
        },
        linePath(field) {
            if (!this.points.length) return '';
            return this.points
                .map((p, i) => (i === 0 ? 'M' : 'L') + this.xOf(i) + ',' + this.yOf(p[field]))
                .join(' ');
        }
    }
};
</script>

<style lang="scss" scoped>
.mtrend {
    &__head {
        display: flex;
        align-items: center;
        padding: 10px 14px;
        border-bottom: 1px solid #eef1f4;
    }
    &__body { padding: 14px; }
    &__chart { position: relative; }
    &__svg {
        width: 100%;
        height: 240px;
        display: block;
    }
    &__axis {
        position: relative;
        height: 34px;
        span {
            position: absolute;
            transform: translateX(-50%);
            font-size: 11px;
            color: #97a3ae;
            white-space: nowrap;
            text-align: center;
            b { display: block; color: #5a6b7b; font-weight: 600; }
            em { display: block; font-style: normal; color: #2b6cb0; font-size: 10px; }
        }
    }
    &__legend {
        display: flex;
        gap: 14px;
        font-size: 12px;
        color: #5a6b7b;
        .dot {
            display: inline-block;
            width: 10px;
            height: 3px;
            border-radius: 2px;
            margin-right: 5px;
            vertical-align: middle;
        }
    }
    &__detail {
        margin-top: 10px;
        padding: 8px 12px;
        background: #f7f9fb;
        border-radius: 4px;
        font-size: 12px;
        color: #5a6b7b;
        min-height: 34px;
        display: flex;
        align-items: center;
        gap: 18px;
        b { color: #2c3e50; }
        &.is-empty { color: #97a3ae; }
    }
}
</style>
